<script setup lang="ts">
import { useScroll } from '@vueuse/core'

export type ModalVariants = 'popup' | 'slide'
type ModalProps = {
  variant?: ModalVariants
  isLarge?: boolean
  group?: string
  show: boolean
  slideLeft?: boolean
  isFullscreen?: boolean
  clearCampaignOnClose?: boolean
  /** Hide the header border until the content scrolls. */
  borderOnScroll?: boolean
  gaOptions?: DataLayerOptional
}

const props = withDefaults(defineProps<ModalProps>(), {
  variant: 'slide',
  isLarge: false,
  prerender: false,
  slideLeft: false,
  isFullscreen: false,
  clearCampaignOnClose: false,
  borderOnScroll: false,
})

const injectedGaOptions = inject('gaOptions', {
  type: Object,
  group: props?.group || '',
})

const emit = defineEmits(['close'])

const { isTopmostModal, pushOpenModal, popOpenModal } = useModalStack()

const locale = useI18n().locale.value
const dialogRef = ref<HTMLDialogElement | null>(null)
const modalRef = ref<HTMLElement | null>(null)
const firstFocusableElement = ref<HTMLElement | null>(null)
const lastFocusableElement = ref<HTMLElement | null>(null)
const previousActiveElement = ref<HTMLElement | null>(null)
const triggerType = ref('')
const { y: scrollY } = useScroll(() => (props.borderOnScroll ? dialogRef.value : null))

const setFocusableElements = () => {
  if (!modalRef.value) return
  const focusableElements = modalRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  )

  // SAFETY: the query selects only HTML tags and attribute-bearing elements, never SVG or MathML.
  firstFocusableElement.value = focusableElements[0] as HTMLElement
  // SAFETY: same query, so the last match is an HTML element too.
  lastFocusableElement.value = focusableElements[focusableElements.length - 1] as HTMLElement
}
const setupFocusTrap = () => {
  if (!modalRef.value) return

  // SAFETY: focus can only be held by an element in the rendered document, which is all HTML here.
  previousActiveElement.value = document.activeElement as HTMLElement
  setFocusableElements()

  setTimeout(() => {
    firstFocusableElement.value?.focus()
  }, 100)
}

const handleTabKey = (e: KeyboardEvent) => {
  if (!modalRef.value || !props.show) return

  if (e.key === 'Tab') {
    setFocusableElements()
    if (e.shiftKey && document.activeElement === firstFocusableElement.value) {
      e.preventDefault()
      lastFocusableElement.value?.focus()
    } else if (!e.shiftKey && document.activeElement === lastFocusableElement.value) {
      e.preventDefault()
      firstFocusableElement.value?.focus()
    }
  }

  if (e.key === 'Escape' && isTopmostModal()) {
    close()
    onClose()
  }
}

const open = () => {
  triggerType.value = ''
  pushOpenModal()
  document?.documentElement?.classList?.add('scroll-lock')
  dialogRef.value?.show()

  setupFocusTrap()
}

const close = () => {
  popOpenModal()
  dialogRef.value?.close()
  if (document.querySelectorAll('.modal-slide[open]').length === 0) {
    // Remove scroll-lock only if this is the last open modal
    document?.documentElement?.classList?.remove('scroll-lock')
  }

  previousActiveElement.value?.focus()
}

const onClose = () => emit('close')

const onBackdropClose = () => {
  triggerType.value = 'background close'
  close()
  onClose()
}

onMounted(() => {
  document.addEventListener('keydown', handleTabKey)
})

onBeforeUnmount(() => {
  popOpenModal()
  document.removeEventListener('keydown', handleTabKey)
})

watch(
  () => props.show,
  (newShowValue, _) => {
    const view = newShowValue ? 'view' : 'close'
    const event = `custom.modal.${view}`
    let type = 'link click'

    if (newShowValue) {
      open()
    } else {
      close()
      type = triggerType.value === 'background close' ? 'background close' : 'close button'
    }

    // Callers can override the close type when another action dismisses the modal.
    // SAFETY: `event` is assigned one of the modal open/close names above, both DataLayerEventName.
    pushEventToDataLayer(event as DataLayerEventName, { ...injectedGaOptions, type, ...props.gaOptions }, locale)

    if (!newShowValue && props.clearCampaignOnClose) useCampaignAttribution().clearCampaign()
  },
)
</script>

<template>
  <div v-if="props.show" class="modal-backdrop" data-testid="modal-backdrop" @click="onBackdropClose"></div>
  <dialog
    ref="dialogRef"
    v-bind="$attrs"
    :class="[
      'modal',
      `modal-${variant}`,
      isLarge ? `modal-${variant}--large` : `modal-${variant}--small`,
      slideLeft ? 'slide--left' : '',
      isFullscreen ? 'modal-fullscreen' : '',
      { 'modal--border-on-scroll': borderOnScroll, 'modal--scrolled': borderOnScroll && scrollY > 0 },
    ]"
    @close="onClose"
  >
    <client-only>
      <div ref="modalRef" v-show="props.show" class="modal-wrapper">
        <div class="modal__header">
          <slot name="header"></slot>
          <button
            id="closeButton"
            data-testid="modal-close"
            :aria-label="$t('aria_label.close_dialog')"
            class="modal__close"
            autofocus
            @click="close"
          >
            <svg-icon aria-hidden="true" name="mhj:close" />
          </button>
        </div>
        <section class="modal__body">
          <slot></slot>
        </section>
        <div v-if="$slots.footer" class="modal__footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </client-only>
  </dialog>
</template>

<style lang="postcss">
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: var(--z-modal-overlay);
  animation: backdrop-fade 0.5s cubic-bezier(0.25, 0, 0.3, 1) forwards;
}

.modal {
  --menu-header-height: 2.6rem;
  background-color: var(--surface-light-00);
  border: none;
  outline: none;
  padding: 0;
  position: fixed;
  z-index: var(--z-modal);

  .modal-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;

    .modal__header {
      height: var(--menu-header-height);
      display: flex;
      align-items: center;
      position: sticky;
      top: 0;
      background-color: var(--surface-light-00);
      z-index: 2;
      flex-shrink: 0;
    }

    .modal__body {
      padding: var(--space-60);
      flex: 1;
    }

    .modal__footer {
      display: flex;
      align-items: center;
      position: sticky;
      bottom: 0;
      padding: var(--space-60);
      z-index: 1;
      flex-shrink: 0;
      background-color: var(--surface-light-00);
      border-top: 1px solid var(--border-break);
    }
  }

  .modal__close {
    aspect-ratio: 1;
    width: 2.25rem;
    font-size: 2.25rem;
    position: absolute;
    right: var(--space-50);
    top: var(--space-50);
    z-index: 2;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-items: center;
    padding: 0;
    svg.icons {
      width: 2.25rem;
      height: 2.25rem;
    }
    @media (--tablet) {
      width: 1.5rem;
      font-size: 1.5rem;
      svg.icons {
        width: 1.5rem;
        height: 1.5rem;
      }
    }
  }
}

.modal-popup {
  width: calc(100% - var(--space-20));
  min-width: 340px;
  max-width: 440px;
  min-height: 320px;
  max-height: calc(100% - 32px);
  overflow: auto;
  &.modal-fullscreen {
    width: 100%;
    height: 100%;
    height: 100dvh;
    max-height: 100%;
    max-height: 100dvh;
    max-width: 100%;
    overflow: hidden;
    position: fixed;
    top: 0;
    --menu-header-height: 0;
    .modal__body {
      padding: 0;
    }
    .modal__close {
      background-color: hsla(0, 0%, 96%, 0.4);
      top: 0;
      right: 0;
      min-width: fit-content;
      min-height: fit-content;
      padding: var(--space-30);
      &:hover {
        background-color: var(--surface-light-20);
      }
      svg.icons {
        width: 2.25rem;
        height: 2.25rem;
      }
      @media (--tablet) {
        padding: var(--space-50);
        svg.icons {
          width: var(--space-90);
          height: var(--space-90);
        }
      }
    }
  }
}

.modal-popup--large {
  max-width: 40rem;
}

.modal-slide {
  --menu-header-height: 3.625rem;
  inset: 0 0 auto auto;
  width: 100%;
  max-width: 25.875rem; /*414px */
  height: 100%;
  height: 100dvh;
  overflow: auto;
  scrollbar-gutter: stable;
  scrollbar-color: var(--border-neutral-30) var(--surface-light-00);

  .modal__header {
    border-bottom: 1px solid var(--border-neutral-20);
  }

  .modal__body {
    padding-bottom: 7rem; /* For iOS with bottom Nav overlapping  */
  }

  @media (--desktop) {
    scrollbar-width: thin;

    .modal__body {
      padding-right: var(--space-40); /* Reduced padding for fixed scrollbar space */
      padding-bottom: 2rem;
    }
  }

  &.slide--left {
    inset: 0 auto auto 0;
  }
}

.modal.modal--border-on-scroll {
  /* Sized to its content so the sticky header stays put past the first screen. */
  .modal-wrapper {
    height: auto;
    min-height: 100%;
  }

  .modal__header {
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease-out;
  }

  &.modal--scrolled .modal__header {
    border-bottom-color: var(--border-neutral-20);
  }
}

/* ------ modal animation styles  -------- */
.modal-popup {
  animation: slide-down 0.3s cubic-bezier(0.25, 0, 0.3, 1);
}

.modal-popup[open] {
  animation: slide-up 0.5s cubic-bezier(0.25, 0, 0.3, 1);
}

.modal-slide {
  animation: slide-out-left 0.3s cubic-bezier(0.25, 0, 0.3, 1);

  &.slide--left {
    animation: slide-out-to-left 0.3s cubic-bezier(0.25, 0, 0.3, 1);
  }
}

.modal-slide[open] {
  animation: slide-in-left 0.5s cubic-bezier(0.25, 0, 0.3, 1);

  &.slide--left {
    animation: slide-in-from-left 0.5s cubic-bezier(0.25, 0, 0.3, 1);
  }
}
</style>
